import { useState, useMemo } from 'react'
import { Spinner } from '@heroui/react'
import { MonthPeriodPicker } from '../reports/shared/MonthPeriodPicker'
import { PhenomenaLegend } from '../reports/shared/PhenomenaLegend'
import { buildDayKeys } from '../reports/shared/phenomenaUtils'
import { GroupJevyOverviewGrid } from './GroupJevyOverviewGrid'
import { GroupJevyDetailGrid } from './GroupJevyDetailGrid'
import { GroupLoadGate } from './shared/GroupLoadGate'
import { HeatLegend } from './shared/HeatLegend'
import { StationLabelPicker } from './shared/StationLabelFields'
import { useMonthPeriodStore } from '../../store/useMonthPeriodStore'
import { useGroupJevyStore } from '../../store/useGroupJevyStore'
import { useStationLabelStore } from '../../store/useStationLabelStore'
import { useTableOrientationStore } from '../../store/useTableOrientationStore'
import { TableOrientationToggle } from '../reports/shared/TableOrientationToggle'
import { usePhenomenaDefinitions, PHENO_CODES } from '../../hooks/usePhenomenaData'
import { useGroupPhenomenaData } from '../../hooks/useGroupPhenomenaData'
import { unionNodeGhIds } from '../../utils/computeGroupTree'
import { PHENO_LEGEND_MAP } from '../reports/shared/phenomenaUtils'
import { GROUP_PERIOD_GATE_THRESHOLD } from '../../config/groupPeriodConfig'

// Skupinový produkt "Jevy – skupina" (/station-group/grp-jevy) — 2 záložky:
//   Přehled = matice stanice × jev (počet dní s jevem), s rozbalením na denní detail
//   Detail  = 1 vybraný jev, stanice × den
// Viz doc/plans/station-group-monthly-yearly-phenomena.md sekce 5.
export function StationGroupJevyView({ selectedNode, selectedPath, stationsByGhId, filteredTree }) {
  const { year, month, setYear, setMonth } = useMonthPeriodStore()
  const { activeTab, setActiveTab, selectedPheno, setSelectedPheno } = useGroupJevyStore()
  const { labelFields, setLabelFields } = useStationLabelStore()
  const orientation = useTableOrientationStore((s) => s.getOrientation('grp-jevy'))

  const isSingleLeaf = !!selectedNode && selectedNode.children.length === 0
  const nodes = selectedNode ? (isSingleLeaf ? [selectedNode] : selectedNode.children) : filteredTree

  const ghIds = useMemo(() => {
    const set = new Set()
    for (const n of nodes) for (const gh of unionNodeGhIds(n)) set.add(gh)
    return [...set]
  }, [nodes])

  const gateKey = `${[...ghIds].sort().join(',')}`
  const [confirmedKey, setConfirmedKey] = useState(null)
  const needsGate = !isSingleLeaf && ghIds.length > GROUP_PERIOD_GATE_THRESHOLD
  const confirmed = !needsGate || confirmedKey === gateKey

  const { defsMap, isLoading: defsLoading, isError: defsError } = usePhenomenaDefinitions()
  const { dayMap, countMap, isLoading: dataLoading, isError: dataError } = useGroupPhenomenaData(ghIds, year, month, confirmed)
  const isLoading = defsLoading || dataLoading
  const isError = defsError || dataError

  const dayKeys = useMemo(() => buildDayKeys(year, month), [year, month])
  const heading = selectedPath.length > 0 ? selectedPath[selectedPath.length - 1].bucketLabel : 'Všechny stanice'
  const phenoDef = { code: selectedPheno, ...(defsMap.get(selectedPheno) ?? {}) }

  const maxCount = useMemo(() => {
    let m = 0
    for (const counts of countMap.values()) for (const n of counts.values()) m = Math.max(m, n)
    return m
  }, [countMap])

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-200 bg-white flex flex-col gap-3">
        <h2 className="text-sm font-semibold text-gray-700">{heading} — Jevy</h2>
        <MonthPeriodPicker
          year={year}
          month={month}
          onYearChange={setYear}
          onMonthChange={setMonth}
          obsType="DLY"
        />
        <div className="flex items-center gap-4 flex-wrap">
          <TableOrientationToggle slug="grp-jevy" />
          <StationLabelPicker labelFields={labelFields} onChange={setLabelFields} />
        </div>

        <div className="flex gap-2 border-b border-gray-100 -mb-4 pb-0">
          {[['overview', 'Přehled'], ['detail', 'Detail']].map(([tab, label]) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-1.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {activeTab === 'detail' && (
          <div className="flex flex-wrap gap-1 pt-1">
            {PHENO_CODES.map(code => {
              const def = defsMap.get(code)
              return (
                <button
                  key={code}
                  onClick={() => setSelectedPheno(code)}
                  title={PHENO_LEGEND_MAP.get(code) ?? def?.name ?? code}
                  className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${
                    selectedPheno === code ? 'bg-blue-500 border-blue-500 text-white' : 'bg-white border-gray-300 text-gray-600 hover:border-blue-400'
                  }`}
                >
                  {def?.svg && <span className="inline-block w-3.5 h-3.5" dangerouslySetInnerHTML={{ __html: def.svg }} />}
                  {code}
                </button>
              )
            })}
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 flex flex-col">
        {!confirmed && (
          <GroupLoadGate
            stationCount={ghIds.length}
            requestNote={`Načte se ${ghIds.length} požadavků na denní jevy.`}
            onConfirm={() => setConfirmedKey(gateKey)}
          />
        )}
        {confirmed && (
          <div className="flex-1 min-h-0 p-4 flex gap-6 overflow-hidden">
            {isLoading && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Spinner size="sm" /> Načítání dat…
              </div>
            )}
            {isError && <p className="text-red-500 text-sm">Chyba při načítání dat</p>}
            {!isLoading && !isError && activeTab === 'overview' && (
              <>
                <div className="flex-1 min-w-0">
                  <GroupJevyOverviewGrid
                    nodes={nodes}
                    stationsByGhId={stationsByGhId}
                    countMap={countMap}
                    dayMap={dayMap}
                    defsMap={defsMap}
                    dayKeys={dayKeys}
                    labelFields={labelFields}
                    orientation={orientation}
                  />
                </div>
                <div className="shrink-0 flex flex-col gap-4">
                  <HeatLegend variant="sequential" maxCount={maxCount} />
                  <PhenomenaLegend defsMap={defsMap} />
                </div>
              </>
            )}
            {!isLoading && !isError && activeTab === 'detail' && (
              <div className="flex-1 min-w-0">
                <GroupJevyDetailGrid
                  nodes={nodes}
                  stationsByGhId={stationsByGhId}
                  dayMap={dayMap}
                  dayKeys={dayKeys}
                  phenoDef={phenoDef}
                  labelFields={labelFields}
                  orientation={orientation}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
